import type { TransportMetadata } from "@hallyulytics/contracts";
import {
  loadAndActivateLocale,
  normalizeLocale,
  sourceLocale,
  type SupportedLocale,
} from "@hallyulytics/i18n";
import { StrictMode, startTransition } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { renderToReadableStream } from "react-dom/server";
import {
  createBrowserRouter,
  createStaticHandler,
  createStaticRouter,
  RouterProvider,
  StaticRouterProvider,
  type RouteObject,
} from "react-router";
import { DeferredScripts, dehydrateDeferred, rehydrateDeferred } from "./deferred";
import { escapeHydrationJson } from "./index";

export interface RenderRequest {
  readonly url: string;
  readonly locale: SupportedLocale;
  readonly nonce: string;
  readonly theme: "light" | "dark" | "system";
  readonly transport: TransportMetadata;
  readonly clientScript: string;
  readonly stylesheet: string;
  readonly routeData: unknown;
  readonly deferMs: number;
}

export function clientLocale(): SupportedLocale {
  return normalizeLocale(document.documentElement.lang) ?? sourceLocale;
}

export async function mountApplication(routes: RouteObject[]): Promise<void> {
  await loadAndActivateLocale(clientLocale());
  const root = document.getElementById("root");
  if (!root) throw new Error("Missing client-render root");
  const router = createBrowserRouter(routes);
  startTransition(() => {
    createRoot(root).render(
      <StrictMode>
        <RouterProvider router={router} />
      </StrictMode>,
    );
  });
}

export async function hydrateApplication(routes: RouteObject[]): Promise<void> {
  if (document.documentElement.dataset.ssr === "fallback") {
    await mountApplication(routes);
    return;
  }
  await loadAndActivateLocale(clientLocale());
  rehydrateDeferred();
  const router = createBrowserRouter(routes);
  startTransition(() => {
    hydrateRoot(
      document,
      <StrictMode>
        <RouterProvider router={router} />
      </StrictMode>,
    );
  });
}

export async function renderApplication(
  routes: RouteObject[],
  input: RenderRequest,
  title: string,
): Promise<ReadableStream<Uint8Array>> {
  await loadAndActivateLocale(input.locale);
  const handler = createStaticHandler(routes);
  const request = new Request(input.url, { headers: { "x-hallyulytics-locale": input.locale } });
  const context = await handler.query(request);
  if (context instanceof Response) throw context;
  const router = createStaticRouter(handler.dataRoutes, context);
  const { loaderData, deferred } = dehydrateDeferred(context.loaderData);
  const hydration = escapeHydrationJson({
    locale: input.locale,
    theme: input.theme,
    transport: input.transport,
  });
  return renderToReadableStream(
    <html
      lang={input.locale.replace("_", "-")}
      className={input.theme === "dark" ? "dark" : undefined}
    >
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <meta property="csp-nonce" content={input.nonce} />
        <title>{title}</title>
        <link rel="stylesheet" href={input.stylesheet} />
      </head>
      <body>
        <StaticRouterProvider
          router={router}
          context={{ ...context, loaderData }}
          nonce={input.nonce}
        />
        <script
          nonce={input.nonce}
          dangerouslySetInnerHTML={{ __html: `window.__HALLYULYTICS__=${hydration}` }}
        />
        <DeferredScripts deferred={deferred} nonce={input.nonce} />
      </body>
    </html>,
    {
      bootstrapModules: [input.clientScript],
      nonce: input.nonce,
      onError: (error) => console.error(error),
    },
  );
}
